import { SHAREPOINT_LIBRARIES, SP_SITE_URL } from '../../../../constants/sharepoint';

export type CcpPhotoSection = 'CCP_OPRP' | 'SIEVES';

interface StoredPhoto {
    id: string;
    tourId: string;
    cycle: string;
    section: CcpPhotoSection;
    fileName: string;
    mimeType: string;
    uploaded: boolean;
}

const DB_NAME = 'ccp_oprp_sieves_photos';
const STORE = 'photos';
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'id' });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function runStore<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    return openDb().then(db => new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const request = work(store);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        tx.oncomplete = () => db.close();
        tx.onerror = () => {
            db.close();
            reject(tx.error);
        };
    }));
}

function safeFileName(name: string): string {
    return name.replace(/[^\w.\-]+/g, '_');
}

async function uploadToSharePoint(file: File, uploadName: string, accessToken: string): Promise<boolean> {
    const libraryName = SHAREPOINT_LIBRARIES.CCP_OPRP_SIEVES;
    const uploadUrl = `${SP_SITE_URL}/_api/web/lists/getByTitle('${libraryName}')/RootFolder/Files/add(url='${encodeURIComponent(uploadName)}',overwrite=true)`;
    const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
            Accept: 'application/json;odata=verbose',
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/octet-stream',
        },
        body: await file.arrayBuffer(),
    });
    if (!response.ok) {
        const detail = await response.text();
        throw new Error(`SharePoint photo upload failed (${response.status}): ${detail}`);
    }
    return true;
}

async function deleteCyclePhotos(tourId: string, cycle: string, section: CcpPhotoSection): Promise<void> {
    const all = await runStore<StoredPhoto[]>('readonly', store => store.getAll());
    const stale = (all || []).filter(photo => photo.tourId === tourId && photo.cycle === cycle && photo.section === section);
    for (const photo of stale) {
        await runStore('readwrite', store => store.delete(photo.id));
    }
}

export async function saveCyclePhotos(options: {
    tourId: string;
    cycle: string;
    section: CcpPhotoSection;
    files: File[];
    accessToken?: string;
}): Promise<string[]> {
    const tourId = options.tourId.replace(/[{}]/g, '');
    await deleteCyclePhotos(tourId, options.cycle, options.section);
    const savedNames: string[] = [];

    for (const file of options.files) {
        const uploadName = `${options.section}_${options.cycle}_${Date.now()}_${safeFileName(file.name)}`;
        let uploaded = false;
        if (navigator.onLine && options.accessToken) {
            try {
                uploaded = await uploadToSharePoint(file, uploadName, options.accessToken);
            } catch (error) {
                console.warn('Photo kept on this device. SharePoint upload did not complete:', error);
            }
        }

        const record: StoredPhoto = {
            id: `${tourId}_${options.section}_${options.cycle}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            tourId,
            cycle: options.cycle,
            section: options.section,
            fileName: file.name,
            mimeType: file.type || 'image/jpeg',
            uploaded,
        };
        await runStore('readwrite', store => store.put(record));
        savedNames.push(file.name);
    }

    return savedNames;
}

export async function getSavedPhotoNames(tourId: string, section: CcpPhotoSection): Promise<Record<string, string[]>> {
    const cleanTourId = tourId.replace(/[{}]/g, '');
    const all = await runStore<StoredPhoto[]>('readonly', store => store.getAll());
    const grouped: Record<string, string[]> = {};
    (all || [])
        .filter(photo => photo.tourId === cleanTourId && photo.section === section)
        .forEach(photo => {
            if (!grouped[photo.cycle]) grouped[photo.cycle] = [];
            grouped[photo.cycle].push(photo.fileName);
        });
    return grouped;
}
